import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  ShoppingCart,
  Package,
  Package2,
  DollarSign,
  Grid3X3,
  BarChart3,
  Users,
  LayoutDashboard,
  Table2,
  UserCog,
  Truck,
  MapPin,
  X,
  ChevronDown,
  ChevronRight,
  Menu,
  ChevronLeft,
  FileText,
  RefreshCw,
  Receipt,
  Search,
  CreditCard,
  Factory,
  ArrowUpDown,
  Tag,
  Settings,
  Sliders,
  Cookie,
  LogOut
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useActiveShift } from '@/hooks/useActiveShift';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

// Mapeo de rutas a módulos de permisos
const routePermissionMap: Record<string, string> = {
  '/dashboard': 'dashboard',
  '/pos': 'pos',
  '/cash-register': 'cash_register',
  '/void-sales': 'void_sales',
  '/customers': 'customers',
  '/promotions': 'promotions',
  '/products': 'products',
  '/ingredients': 'ingredients',
  '/locations': 'locations',
  '/employees': 'employees',
  '/suppliers': 'suppliers',
  '/stock-config': 'stock_config',
  '/system-parameters': 'system_parameters',
  '/purchase-orders': 'purchase_orders',
  '/distribution': 'distribution',
  '/inventory': 'inventory',
  '/kardex': 'kardex',
  '/transformations': 'transformations',
  '/accounts-payable': 'accounts_payable',
  '/merchandise-movements': 'merchandise_movements',
  '/discrepancy-review': 'discrepancy_review',
  '/bread-transfers': 'bread_transfers',
  '/vouchers/manage': 'vouchers',
  '/reports': 'reports'
};

const navigationItems = [
  {
    path: '/dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    testId: 'nav-icon-dashboard'
  },
  {
    label: 'Venta',
    icon: ShoppingCart,
    testId: 'nav-section-venta',
    children: [
      {
        path: '/pos',
        label: 'Punto de Venta',
        icon: ShoppingCart,
        testId: 'nav-icon-pos'
      },
      {
        path: '/cash-register',
        label: 'Caja',
        icon: DollarSign,
        testId: 'nav-icon-cash-register'
      },
      {
        path: '/customers',
        label: 'Clientes',
        icon: Users,
        testId: 'nav-icon-customers'
      },
      {
        path: '/promotions',
        label: 'Promociones',
        icon: Tag,
        testId: 'nav-icon-promotions'
      },
    ]
  },
  {
    label: 'Maestros',
    icon: Package,
    testId: 'nav-section-maestros',
    children: [
      {
        path: '/products',
        label: 'Productos',
        icon: Grid3X3,
        testId: 'nav-icon-products'
      },
      {
        path: '/ingredients',
        label: 'Insumos',
        icon: Package2,
        testId: 'nav-icon-ingredients'
      },
      {
        path: '/locations',
        label: 'Sucursales',
        icon: MapPin,
        testId: 'nav-icon-locations'
      },
      {
        path: '/employees',
        label: 'Empleados',
        icon: UserCog,
        testId: 'nav-icon-employees'
      },
      {
        path: '/suppliers',
        label: 'Proveedores',
        icon: Factory,
        testId: 'nav-icon-suppliers'
      },
      {
        path: '/stock-config',
        label: 'Configuración Stock',
        icon: Settings,
        testId: 'nav-icon-stock-config'
      },
      {
        path: '/system-parameters',
        label: 'Parámetros',
        icon: Sliders,
        testId: 'nav-icon-system-parameters'
      }
    ]
  },
  {
    label: 'Administración',
    icon: Table2,
    testId: 'nav-section-administracion',
    children: [
      {
        path: '/purchase-orders',
        label: 'Órdenes de compra',
        icon: ShoppingCart,
        testId: 'nav-icon-purchase-orders'
      },
      {
        path: '/distribution',
        label: 'Transferencias',
        icon: Truck,
        testId: 'nav-icon-distribution'
      },
      {
        path: '/inventory',
        label: 'Inventario',
        icon: Package,
        testId: 'nav-icon-inventory'
      },
      {
        path: '/kardex',
        label: 'Kardex de Insumos',
        icon: FileText,
        testId: 'nav-icon-kardex'
      },
      {
        path: '/transformations',
        label: 'Transformaciones',
        icon: RefreshCw,
        testId: 'nav-icon-transformations'
      },
      {
        path: '/accounts-payable',
        label: 'Cuentas por Pagar',
        icon: CreditCard,
        testId: 'nav-icon-accounts-payable'
      },
      {
        path: '/merchandise-movements',
        label: 'Movimientos de Mercadería',
        icon: ArrowUpDown,
        testId: 'nav-icon-merchandise-movements'
      },
      {
        path: '/bread-transfers',
        label: 'Transferencias de Pan',
        icon: Cookie,
        testId: 'nav-icon-bread-transfers'
      },
      {
        path: '/discrepancy-review',
        label: 'Revisión de Diferencias',
        icon: Search,
        testId: 'nav-icon-discrepancy-review'
      }
    ]
  },
  {
    path: '/vouchers/manage',
    label: 'Comprobantes',
    icon: Receipt,
    testId: 'nav-icon-vouchers'
  },
  {
    path: '/reports',
    label: 'Reportes',
    icon: BarChart3,
    testId: 'nav-icon-reports'
  }
];

interface SidebarProps {
  isOpen?: boolean;
  onToggle?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  isOpen = false,
  onToggle,
  isCollapsed = false,
  onToggleCollapse
}) => {
  const { selectedLocation, user, logout } = useAuth();
  const { shiftName } = useActiveShift();
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const sidebarRef = React.useRef<HTMLDivElement>(null);
  const buttonRefs = React.useRef<Record<string, HTMLButtonElement | null>>({});

  // Identificar si el usuario es cajero
  const isCajero = user?.positionId === 3;

  // Detectar clics fuera del sidebar para cerrar submenus cuando está contraído
  React.useEffect(() => {
    if (!isCollapsed) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        setExpandedSections({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCollapsed]);

  // Función para verificar si el usuario tiene permisos para una ruta
  const hasPermissionForRoute = (path: string): boolean => {
    if (!user?.permisos || !Array.isArray(user.permisos)) {
      return false;
    }

    const requiredModule = routePermissionMap[path];
    if (!requiredModule) {
      return true; // Si no está mapeado, permitir acceso por defecto
    }

    // Verificar si el usuario tiene permiso para este módulo
    return user.permisos.some((permiso: any) => permiso.module === requiredModule);
  };

  // Filtrar elementos de navegación basándose en permisos
  const filterNavigationItems = (items: any[]): any[] => {
    return items.filter(item => {
      // Si el item tiene un path directo, verificar permisos
      if (item.path) {
        return hasPermissionForRoute(item.path);
      }

      // Si el item tiene hijos, filtrar los hijos
      if (item.children) {
        const filteredChildren = item.children.filter((child: any) =>
          hasPermissionForRoute(child.path)
        );

        // Solo incluir la sección padre si tiene al menos un hijo permitido
        if (filteredChildren.length > 0) {
          return { ...item, children: filteredChildren };
        }
        return false;
      }

      return true;
    }).map(item => {
      // Aplicar el filtrado de hijos si es necesario
      if (item.children) {
        const filteredChildren = item.children.filter((child: any) =>
          hasPermissionForRoute(child.path)
        );
        return { ...item, children: filteredChildren };
      }
      return item;
    });
  };

  const allowedNavigationItems = filterNavigationItems(navigationItems);

  // Función para aplanar el menú (quitar padres y mostrar solo hijos) para cajeros
  const flattenNavigationItems = (items: any[]): any[] => {
    const flattened: any[] = [];

    items.forEach(item => {
      if (item.children && Array.isArray(item.children)) {
        // Si tiene hijos, agregar solo los hijos al array
        flattened.push(...item.children);
      } else {
        // Si no tiene hijos, agregar el item tal cual
        flattened.push(item);
      }
    });

    return flattened;
  };

  // Si es cajero, aplanar el menú para mostrar solo los items sin secciones padre
  const finalNavigationItems = isCajero
    ? flattenNavigationItems(allowedNavigationItems)
    : allowedNavigationItems;

  // Debug logging
  React.useEffect(() => {
    console.log('🔍 User permissions:', user?.permisos);
    console.log('🔍 Is Cajero:', isCajero);
    console.log('🔍 Allowed navigation items:', allowedNavigationItems);
    console.log('🔍 Final navigation items:', finalNavigationItems);
  }, [user?.permisos, allowedNavigationItems, isCajero, finalNavigationItems]);

  const toggleSection = (sectionLabel: string) => {
    setExpandedSections(prev => {
      const isCurrentlyExpanded = prev[sectionLabel];

      // Si la sección ya está expandida, contraerla
      if (isCurrentlyExpanded) {
        return {
          ...prev,
          [sectionLabel]: false
        };
      }

      // Si la sección está contraída, contraer todas las demás y expandir esta
      const newState: Record<string, boolean> = {};
      Object.keys(prev).forEach(key => {
        newState[key] = false; // Contraer todas las secciones
      });
      newState[sectionLabel] = true; // Expandir solo la sección clickeada

      return newState;
    });
  };

  const renderNavItem = (item: any, isChild = false) => {
    const Icon = item.icon;
    
    if (item.children) {
      const isExpanded = expandedSections[item.label];
      const ChevronIcon = isExpanded ? ChevronDown : ChevronRight;
      
      if (isCollapsed) {
        const buttonRect = buttonRefs.current[item.label]?.getBoundingClientRect();

        return (
          <li key={item.label} className="relative">
            <button
              ref={(el) => {
                buttonRefs.current[item.label] = el;
              }}
              onClick={() => toggleSection(item.label)}
              className={cn(
                'flex items-center justify-center w-full px-2 py-3 rounded-lg text-sm font-medium transition-colors duration-200',
                'hover:bg-macartur-primary/20 hover:text-white text-gray-300',
                expandedSections[item.label] && 'bg-macartur-primary/20'
              )}
              title={item.label}
            >
              {Icon && (
                <Icon
                  className="w-5 h-5 shrink-0"
                  data-testid={item.testId}
                />
              )}
            </button>

            {/* Submenu flotante para sidebar contraído - posicionado con fixed */}
            {(expandedSections[item.label] && buttonRect) && (
              <div
                className="fixed z-[100]"
                style={{
                  left: `${buttonRect.right + 8}px`,
                  top: `${buttonRect.top}px`
                }}
              >
                <div className="bg-macartur-accent1 border border-macartur-primary/30 rounded-lg shadow-xl py-2 min-w-48">
                  <div className="px-4 py-2 text-sm font-medium text-gray-300 border-b border-macartur-primary/30">
                    {item.label}
                  </div>
                  {item.children.map((child: any) => (
                    <NavLink
                      key={child.path}
                      to={child.path}
                      onClick={() => {
                        if (onToggle && window.innerWidth < 1024) {
                          onToggle();
                        }
                        setExpandedSections(prev => ({ ...prev, [item.label]: false }));
                      }}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center px-4 py-2 text-sm transition-colors duration-200',
                          'hover:bg-macartur-primary/20 hover:text-white',
                          isActive
                            ? 'bg-macartur-primary text-white'
                            : 'text-gray-300'
                        )
                      }
                    >
                      <child.icon className="w-4 h-4 mr-3" />
                      {child.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            )}
          </li>
        );
      }
      
      return (
        <li key={item.label}>
          <button
            onClick={() => toggleSection(item.label)}
            className={cn(
              'flex items-center justify-between w-full px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200',
              'hover:bg-macartur-primary/20 hover:text-white text-gray-300'
            )}
          >
            <div className="flex items-center">
              <Icon 
                className="w-5 h-5 mr-3" 
                data-testid={item.testId}
              />
              {item.label}
            </div>
            <ChevronIcon className="w-4 h-4" />
          </button>
          
          {isExpanded && (
            <ul className="ml-6 mt-2 space-y-1">
              {item.children.map((child: any) => renderNavItem(child, true))}
            </ul>
          )}
        </li>
      );
    }

    return (
      <li key={item.path}>
        <NavLink
          to={item.path}
          onClick={() => {
            if (onToggle && window.innerWidth < 1024) {
              onToggle();
            }
          }}
          className={({ isActive }) =>
            cn(
              'flex items-center rounded-lg text-sm font-medium transition-colors duration-200',
              'hover:bg-macartur-primary/20 hover:text-white',
              isActive
                ? 'bg-macartur-primary text-white'
                : 'text-gray-300',
              isChild && 'text-xs pl-8',
              isCollapsed && !isChild ? 'justify-center px-2 py-3' : 'px-4 py-3'
            )
          }
          title={isCollapsed ? item.label : undefined}
        >
          {Icon && (
            <Icon
              className={cn(
                isChild ? "w-4 h-4" : "w-5 h-5",
                "shrink-0",
                isCollapsed && !isChild ? "" : "mr-3"
              )}
              data-testid={item.testId}
            />
          )}
          {!isCollapsed && item.label}
        </NavLink>
      </li>
    );
  };

  return (
    <>
      {/* Mobile/Tablet Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}
      
      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        className={cn(
          "bg-macartur-accent1 text-white flex flex-col transition-all duration-300 z-50",
          // Desktop: always visible, responsive width
          "hidden lg:flex",
          isCollapsed ? "lg:w-16" : "lg:w-64",
          // Tablet: overlay sidebar
          "md:lg:hidden md:fixed md:inset-y-0 md:left-0 md:w-64",
          // Mobile: hidden (uses bottom nav instead)
          "lg:relative lg:translate-x-0",
          // Toggle states
          isOpen
            ? "md:translate-x-0"
            : "md:-translate-x-full"
        )}
      >
        {/* Header */}
        <div className={cn(
          "border-b border-macartur-primary/30 transition-all duration-300",
          isCollapsed ? "p-4" : "p-6"
        )}>
          <div className="flex items-center justify-between mb-3">
            {!isCollapsed && (
              <div>
                <h1 className="text-xl font-bold text-macartur-secondary">Macartur POS</h1>
                {/* Solo mostrar ubicación para CAJEROS (positionId = 3) */}
                {user?.positionId === 3 && (selectedLocation || user?.locationName) && (
                  <p className="text-sm text-gray-300 mt-1">
                    {selectedLocation?.name || user?.locationName}
                  </p>
                )}
              </div>
            )}

            <div className="flex items-center space-x-2">
              {/* Collapse toggle button for desktop */}
              {onToggleCollapse && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onToggleCollapse}
                  className="hidden lg:flex text-gray-400 hover:text-white p-2"
                  title={isCollapsed ? "Expandir menú" : "Contraer menú"}
                >
                  {isCollapsed ? <Menu className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
                </Button>
              )}

              {/* Close button for tablet */}
              <Button
                variant="ghost"
                size="sm"
                onClick={onToggle}
                className="lg:hidden text-gray-400 hover:text-white p-2"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* User and Shift - Turno solo para CAJEROS */}
          {!isCollapsed && user && (
            <div className="text-sm text-white pt-2 border-t border-macartur-primary/20">
              <div className="font-medium">{user.nombre} {user.apellidos}</div>
              {user.positionId === 3 && shiftName && <div>Turno: {shiftName}</div>}
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 overflow-y-auto">
          <ul className="space-y-2 overflow-visible">
            {finalNavigationItems.map((item) => renderNavItem(item))}
          </ul>
        </nav>

        {/* Logout Button */}
        <div className={cn(
          "border-t border-macartur-primary/30 transition-all duration-300",
          isCollapsed ? "p-2" : "p-4"
        )}>
          <div className={cn(
            "flex items-center",
            isCollapsed ? "justify-center" : "justify-between"
          )}>
            <Button
              variant="ghost"
              onClick={() => logout()}
              className={cn(
                "text-gray-300 hover:text-white hover:bg-red-600/20 transition-colors duration-200",
                isCollapsed
                  ? "px-2 py-3"
                  : "px-4 py-3"
              )}
              title={isCollapsed ? "Cerrar sesión" : undefined}
            >
              <LogOut className={cn(
                "w-5 h-5 shrink-0",
                isCollapsed ? "" : "mr-3"
              )} />
              {!isCollapsed && <span className="font-medium">Cerrar sesión</span>}
            </Button>

            {!isCollapsed && (
              <div className="text-white font-mono font-bold text-xs px-2">
                v1.3.12
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};